/**
 * Admin app settings page for /admin/settings route.
 * Allows admins to configure wizard invite message templates and app-level settings.
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PageHeader } from "../components/layout/PageHeader";
import { useProfile } from "../contexts/ProfileContext";
import { CHEF_ENABLED, publicRoutes } from "../lib/routes";
import { useAdminWorkspaces } from "../hooks/useAdminWorkspaces";
import { GENERATION_CONFIG_KEY, generationSaveError, readGenerationConfig } from "../lib/generationConfig";
import {
  adminGetSettings,
  adminUpdateSettings,
  adminGetAppSettings,
  adminUpdateAppSetting,
  getChefConfig,
  updateChefConfig,
  adminListApiKeys,
  adminCreateApiKey,
  adminRevokeApiKey,
  adminUpdateApiKey,
  type AdminApiKey,
  type WizardInviteTemplates,
} from "../services/naraDoor";
import { Save, Check, Loader2, ChevronDown, ChevronRight, RotateCcw, Download, Key, Plus, Trash2, Copy, Eye, EyeOff, Shield, Globe, Pencil } from "lucide-react";

/** Auto-expanding textarea that grows to fit content (no internal scroll). */
function AutoTextarea({
  value,
  onChange,
  placeholder,
  className,
  minRows = 3,
}: {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  placeholder?: string;
  className?: string;
  minRows?: number;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);

  const resize = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, []);

  useEffect(() => { resize(); }, [value, resize]);

  return (
    <textarea
      ref={ref}
      value={value}
      onChange={(e) => { onChange(e); resize(); }}
      placeholder={placeholder}
      rows={minRows}
      className={className}
      style={{ overflow: "hidden" }}
    />
  );
}

const DEFAULT_TEMPLATES = {
  smsTemplate: "Hi {{ recipient.name }}, {{ sender.name }} invites you to continue here: {{ wizard_link }}",
  emailSubject: "{{ sender.name }} invited you to complete a step",
  emailTemplate: "Hi {{ recipient.name }},\n\n{{ sender.name }} would like you to complete the \"{{ step_name }}\" step.\n\nClick here to continue: {{ wizard_link }}\n\nBest regards,\nThe Nara Team",
};

export function AdminAppSettings() {
  const { t } = useTranslation();
  const { isAdmin, isLoading: isProfileLoading } = useProfile();

  // Each form field is a draft: null/absent follows what the server holds, so a refetch is
  // shown unless the admin is typing in that field. A save clears the draft.
  const [templateDraft, setTemplateDraft] = useState<Partial<WizardInviteTemplates>>({});
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");

  const settingsQuery = useQuery({
    queryKey: ["adminSettings"],
    queryFn: () => adminGetSettings().catch((err) => { console.error("Failed to load admin settings:", err); throw err; }),
  });
  const saved = settingsQuery.data?.wizardInviteTemplates;
  const smsTemplate = templateDraft.smsTemplate ?? (saved?.smsTemplate || DEFAULT_TEMPLATES.smsTemplate);
  const emailSubject = templateDraft.emailSubject ?? (saved?.emailSubject || DEFAULT_TEMPLATES.emailSubject);
  const emailTemplate = templateDraft.emailTemplate ?? (saved?.emailTemplate || DEFAULT_TEMPLATES.emailTemplate);
  const isLoading = settingsQuery.isLoading;
  const editTemplate = (field: keyof WizardInviteTemplates, value: string) =>
    setTemplateDraft((prev) => ({ ...prev, [field]: value }));

  // App settings state
  const appSettingsQuery = useQuery({
    queryKey: ["adminAppSettings"],
    queryFn: () => adminGetAppSettings().catch((err) => { console.error("Failed to load app settings:", err); throw err; }),
  });
  const workspacesQuery = useAdminWorkspaces({ limit: 100 });
  const workspaces = workspacesQuery.data?.workspaces || [];
  const mockSetting = Array.isArray(appSettingsQuery.data) ? appSettingsQuery.data.find((s) => s.key === "mock_workspace_id") : undefined;
  const [mockWorkspaceDraft, setMockWorkspaceDraft] = useState<string | null>(null);
  const mockWorkspaceId = mockWorkspaceDraft ?? (typeof mockSetting?.value === "string" ? mockSetting.value : "");
  const isAppSettingsLoading = appSettingsQuery.isLoading || workspacesQuery.isLoading;
  const appSettingsLoadFailed = appSettingsQuery.isError || workspacesQuery.isError;
  const [appSettingsSaveStatus, setAppSettingsSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");

  // Document generation model: drafts follow the server until typed in
  const generationSaved = readGenerationConfig(appSettingsQuery.data);
  const [generationDraft, setGenerationDraft] = useState<Partial<typeof generationSaved>>({});
  const generation = { ...generationSaved, ...generationDraft };
  const [generationStatus, setGenerationStatus] = useState<"idle" | "saving" | "saved" | "unpriced" | "error">("idle");

  // Chef config state
  // Skipped while NaraChef is disabled — see CHEF_ENABLED
  const chefQuery = useQuery({
    queryKey: ["chefConfig"],
    queryFn: () => getChefConfig().catch((err) => { console.error("Failed to load chef config:", err); throw err; }),
    enabled: CHEF_ENABLED,
  });
  const [chefDraft, setChefDraft] = useState<Record<string, string> | null>(null);
  const savedChefConfigs: Record<string, string> = Object.fromEntries((chefQuery.data?.configs ?? []).map((item) => [item.key, item.value]));
  const chefConfigs = chefDraft ?? savedChefConfigs;
  const isChefConfigLoading = chefQuery.isLoading;
  const [chefConfigSaveStatus, setChefConfigSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [expandedPrompts, setExpandedPrompts] = useState<Record<string, boolean>>({});

  // API keys state
  const apiKeysQuery = useQuery({
    queryKey: ["adminApiKeys"],
    queryFn: () => adminListApiKeys().catch((err) => { console.error("Failed to load API keys:", err); throw err; }),
  });
  const apiKeys = apiKeysQuery.data?.keys || [];
  const isApiKeysLoading = apiKeysQuery.isLoading;
  const [actionError, setActionError] = useState<string | null>(null);
  const apiKeyError = actionError ?? (apiKeysQuery.isError ? "Failed to load API keys" : null);
  const setApiKeyError = setActionError;
  const [newKeyName, setNewKeyName] = useState("");
  const [newKeyIps, setNewKeyIps] = useState("");
  const [isCreatingKey, setIsCreatingKey] = useState(false);
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [keyVisible, setKeyVisible] = useState(false);
  const [keyCopied, setKeyCopied] = useState(false);
  const [isFetchingIp, setIsFetchingIp] = useState(false);
  const [editingKeyId, setEditingKeyId] = useState<string | null>(null);
  const [editKeyName, setEditKeyName] = useState("");
  const [editKeyIps, setEditKeyIps] = useState("");
  const [isUpdatingKey, setIsUpdatingKey] = useState(false);

  const handleSave = async () => {
    setSaveStatus("saving");
    try {
      await adminUpdateSettings({
        wizardInviteTemplates: {
          smsTemplate,
          emailSubject,
          emailTemplate,
        },
      });
      setTemplateDraft({});
      setSaveStatus("saved");
      setTimeout(() => setSaveStatus("idle"), 3000);
    } catch {
      setSaveStatus("error");
      setTimeout(() => setSaveStatus("idle"), 3000);
    }
  };

  const handleAppSettingsSave = async () => {
    setAppSettingsSaveStatus("saving");
    try {
      await adminUpdateAppSetting("mock_workspace_id", mockWorkspaceId || null);
      setMockWorkspaceDraft(null);
      setAppSettingsSaveStatus("saved");
      setTimeout(() => setAppSettingsSaveStatus("idle"), 3000);
    } catch {
      setAppSettingsSaveStatus("error");
      setTimeout(() => setAppSettingsSaveStatus("idle"), 3000);
    }
  };

  const handleGenerationSave = async () => {
    setGenerationStatus("saving");
    try {
      await adminUpdateAppSetting(GENERATION_CONFIG_KEY, { model: generation.model.trim(), location: generation.location.trim() });
      setGenerationDraft({});
      setGenerationStatus("saved");
    } catch (err) {
      const status = generationSaveError(err);
      console.error("Failed to save generation config:", { key: GENERATION_CONFIG_KEY, status });
      setGenerationStatus(status);
    }
    setTimeout(() => setGenerationStatus("idle"), 3000);
  };

  const handleChefConfigSave = async () => {
    setChefConfigSaveStatus("saving");
    try {
      await updateChefConfig(chefConfigs);
      setChefDraft(null);
      setChefConfigSaveStatus("saved");
      setTimeout(() => setChefConfigSaveStatus("idle"), 3000);
    } catch {
      setChefConfigSaveStatus("error");
      setTimeout(() => setChefConfigSaveStatus("idle"), 3000);
    }
  };

  const togglePrompt = (key: string) => {
    setExpandedPrompts((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const updateChefConfigValue = (key: string, value: string) => {
    setChefDraft((prev) => ({ ...(prev ?? savedChefConfigs), [key]: value }));
  };

  const resetChefConfigValue = (key: string) => {
    setChefDraft((prev) => {
      const next = { ...(prev ?? savedChefConfigs) };
      delete next[key];
      return next;
    });
  };

  const handleBackupDownload = () => {
    const backup = {
      exportedAt: new Date().toISOString(),
      chefConfig: chefConfigs,
    };
    const blob = new Blob([JSON.stringify(backup, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `narachef-config-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleCreateApiKey = async () => {
    if (!newKeyName.trim()) return;
    setIsCreatingKey(true);
    setApiKeyError(null);
    try {
      const ips = newKeyIps
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      const result = await adminCreateApiKey({ name: newKeyName.trim(), allowed_ips: ips });
      setCreatedKey(result.key);
      setKeyVisible(true);
      setNewKeyName("");
      setNewKeyIps("");
    } catch (err) {
      console.error("Failed to create API key:", err);
      setApiKeyError("Failed to create API key");
    } finally {
      setIsCreatingKey(false);
    }
  };

  const handleRevokeKey = async (id: string, name: string) => {
    if (!confirm(`Revoke API key "${name}"? This cannot be undone.`)) return;
    setApiKeyError(null);
    try {
      await adminRevokeApiKey(id);
    } catch (err) {
      console.error("Failed to revoke API key:", err);
      setApiKeyError("Failed to revoke API key");
    }
  };

  const handleCopyKey = async () => {
    if (!createdKey) return;
    try {
      await navigator.clipboard.writeText(createdKey);
      setKeyCopied(true);
      setTimeout(() => setKeyCopied(false), 2000);
    } catch {
      // Fallback
      const el = document.createElement("textarea");
      el.value = createdKey;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
      setKeyCopied(true);
      setTimeout(() => setKeyCopied(false), 2000);
    }
  };

  const handleAddMyIp = async () => {
    setIsFetchingIp(true);
    try {
      const res = await fetch("https://api.ipify.org?format=json");
      const data = await res.json();
      const ip = data.ip as string;
      if (!ip) throw new Error("No IP returned");
      const cidr = `${ip}/32`;
      setNewKeyIps((prev) => {
        const existing = prev.split(",").map((s) => s.trim()).filter(Boolean);
        if (existing.includes(cidr) || existing.includes(ip)) return prev;
        return existing.length > 0 ? `${prev}, ${cidr}` : cidr;
      });
    } catch (err) {
      console.error("Failed to fetch IP:", err);
      setApiKeyError("Failed to detect your IP address");
    } finally {
      setIsFetchingIp(false);
    }
  };

  const handleUpdateKey = async (id: string) => {
    setIsUpdatingKey(true);
    setApiKeyError(null);
    try {
      const ips = editKeyIps
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      await adminUpdateApiKey(id, { name: editKeyName.trim() || undefined, allowed_ips: ips });
      setEditingKeyId(null);
    } catch (err) {
      console.error("Failed to update API key:", err);
      setApiKeyError("Failed to update API key");
    } finally {
      setIsUpdatingKey(false);
    }
  };

  const startEditKey = (key: AdminApiKey) => {
    setEditingKeyId(key.id);
    setEditKeyName(key.name);
    setEditKeyIps(key.allowed_ips.join(", "));
  };

  const cancelEditKey = () => {
    setEditingKeyId(null);
    setEditKeyName("");
    setEditKeyIps("");
  };

  const handleAddMyIpToEdit = async () => {
    setIsFetchingIp(true);
    try {
      const res = await fetch("https://api.ipify.org?format=json");
      const data = await res.json();
      const ip = data.ip as string;
      if (!ip) throw new Error("No IP returned");
      const cidr = `${ip}/32`;
      setEditKeyIps((prev) => {
        const existing = prev.split(",").map((s) => s.trim()).filter(Boolean);
        if (existing.includes(cidr) || existing.includes(ip)) return prev;
        return existing.length > 0 ? `${prev}, ${cidr}` : cidr;
      });
    } catch (err) {
      console.error("Failed to fetch IP:", err);
      setApiKeyError("Failed to detect your IP address");
    } finally {
      setIsFetchingIp(false);
    }
  };

  if (!isProfileLoading && !isAdmin) {
    return <Navigate to={publicRoutes.home} replace />;
  }

  return (
    <div>
      <PageHeader
        title={t("adminSettings.title")}
        description={t("adminSettings.description")}
        isAdmin
        breadcrumbs={[
          { label: t("adminSettings.breadcrumbs.dashboard"), href: publicRoutes.home },
          { label: t("adminSettings.breadcrumbs.admin") },
          { label: t("adminSettings.breadcrumbs.settings") },
        ]}
      />

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 text-muted-foreground animate-spin" />
          <span className="ml-2 text-sm text-muted-foreground">{t("adminSettings.templates.loading")}</span>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Wizard Invite Templates */}
          <div className="bg-card border border-border rounded-xl p-5">
            <h4 className="text-sm font-semibold text-foreground mb-1">{t("adminSettings.templates.title")}</h4>
            <p className="text-xs text-muted-foreground mb-4">{t("adminSettings.templates.description")}</p>
            {settingsQuery.isError && <p className="text-sm text-red-500 mb-4">{t("adminSettings.templates.loadError")}</p>}

            <div className="space-y-4">
              {/* SMS Template */}
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">
                  {t("adminSettings.templates.smsTemplate")}
                </label>
                <AutoTextarea
                  value={smsTemplate}
                  onChange={(e) => editTemplate("smsTemplate", e.target.value)}
                  minRows={3}
                  className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background resize-y focus:outline-none focus:ring-2 focus:ring-nara-orange/50"
                />
              </div>

              {/* Email Subject */}
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">
                  {t("adminSettings.templates.emailSubject")}
                </label>
                <input
                  type="text"
                  value={emailSubject}
                  onChange={(e) => editTemplate("emailSubject", e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-nara-orange/50"
                />
              </div>

              {/* Email Template */}
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">
                  {t("adminSettings.templates.emailTemplate")}
                </label>
                <AutoTextarea
                  value={emailTemplate}
                  onChange={(e) => editTemplate("emailTemplate", e.target.value)}
                  minRows={4}
                  className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background resize-y focus:outline-none focus:ring-2 focus:ring-nara-orange/50"
                />
              </div>

              {/* Available variables */}
              <div className="bg-muted/30 px-3 py-2 rounded-lg">
                <span className="text-xs font-medium text-muted-foreground">{t("adminSettings.templates.variables")}: </span>
                <code className="text-xs font-mono text-foreground">{t("adminSettings.templates.variablesList")}</code>
              </div>

              {/* Save button */}
              <div className="flex items-center gap-3">
                <button
                  onClick={handleSave}
                  disabled={saveStatus === "saving"}
                  className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-nara-orange hover:bg-nara-orange/90 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saveStatus === "saving" ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4" />
                  )}
                  {saveStatus === "saving" ? t("adminSettings.templates.saving") : t("adminSettings.templates.save")}
                </button>
                {saveStatus === "saved" && (
                  <span className="flex items-center gap-1 text-sm text-green-600">
                    <Check className="h-4 w-4" />
                    {t("adminSettings.templates.saved")}
                  </span>
                )}
                {saveStatus === "error" && (
                  <span className="text-sm text-red-500">{t("adminSettings.templates.saveError")}</span>
                )}
              </div>
            </div>
          </div>

          {/* App Settings */}
          <div className="bg-card border border-border rounded-xl p-5">
            <h4 className="text-sm font-semibold text-foreground mb-1">{t("adminSettings.appSettings.title")}</h4>
            <p className="text-xs text-muted-foreground mb-4">{t("adminSettings.appSettings.description")}</p>
            {appSettingsLoadFailed && <p className="text-sm text-red-500 mb-4">{t("adminSettings.appSettings.loadError")}</p>}

            {isAppSettingsLoading ? (
              <div className="flex items-center py-4">
                <Loader2 className="h-4 w-4 text-muted-foreground animate-spin" />
                <span className="ml-2 text-sm text-muted-foreground">{t("adminSettings.appSettings.loading")}</span>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Mock Workspace ID */}
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">
                    {t("adminSettings.appSettings.mockWorkspaceId")}
                  </label>
                  <select
                    value={mockWorkspaceId}
                    onChange={(e) => setMockWorkspaceDraft(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-nara-orange/50"
                  >
                    <option value="">{t("adminSettings.appSettings.selectWorkspace")}</option>
                    {workspaces.map((ws) => (
                      <option key={ws.id} value={ws.id}>
                        {ws.name}{ws.handle ? ` (@${ws.handle})` : ""}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-muted-foreground mt-1">{t("adminSettings.appSettings.mockWorkspaceIdHint")}</p>
                </div>

                {/* Save button */}
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleAppSettingsSave}
                    disabled={appSettingsSaveStatus === "saving"}
                    className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-nara-orange hover:bg-nara-orange/90 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {appSettingsSaveStatus === "saving" ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4" />
                    )}
                    {appSettingsSaveStatus === "saving" ? t("adminSettings.appSettings.saving") : t("adminSettings.appSettings.save")}
                  </button>
                  {appSettingsSaveStatus === "saved" && (
                    <span className="flex items-center gap-1 text-sm text-green-600">
                      <Check className="h-4 w-4" />
                      {t("adminSettings.appSettings.saved")}
                    </span>
                  )}
                  {appSettingsSaveStatus === "error" && (
                    <span className="text-sm text-red-500">{t("adminSettings.appSettings.saveError")}</span>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Document Generation */}
          <div className="bg-card border border-border rounded-xl p-5">
            <h4 className="text-sm font-semibold text-foreground mb-1">{t("adminSettings.generation.title")}</h4>
            <p className="text-xs text-muted-foreground mb-4">{t("adminSettings.generation.description")}</p>
            {appSettingsQuery.isError && <p className="text-sm text-red-500 mb-4">{t("adminSettings.appSettings.loadError")}</p>}

            {appSettingsQuery.isLoading ? (
              <div className="flex items-center py-4">
                <Loader2 className="h-4 w-4 text-muted-foreground animate-spin" />
                <span className="ml-2 text-sm text-muted-foreground">{t("adminSettings.appSettings.loading")}</span>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">
                    {t("adminSettings.generation.model")}
                  </label>
                  <input
                    type="text"
                    value={generation.model}
                    onChange={(e) => setGenerationDraft((prev) => ({ ...prev, model: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-nara-orange/50"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">
                    {t("adminSettings.generation.location")}
                  </label>
                  <input
                    type="text"
                    value={generation.location}
                    onChange={(e) => setGenerationDraft((prev) => ({ ...prev, location: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-nara-orange/50"
                  />
                  <p className="text-xs text-muted-foreground mt-1">{t("adminSettings.generation.locationHelp")}</p>
                </div>

                <div className="flex items-center gap-3">
                  <button
                    onClick={handleGenerationSave}
                    disabled={generationStatus === "saving"}
                    className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-nara-orange hover:bg-nara-orange/90 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {generationStatus === "saving" ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4" />
                    )}
                    {generationStatus === "saving" ? t("adminSettings.appSettings.saving") : t("adminSettings.appSettings.save")}
                  </button>
                  {generationStatus === "saved" && (
                    <span className="flex items-center gap-1 text-sm text-green-600">
                      <Check className="h-4 w-4" />
                      {t("adminSettings.generation.saved")}
                    </span>
                  )}
                  {(generationStatus === "unpriced" || generationStatus === "error") && (
                    <span className="text-sm text-red-500">{t(`adminSettings.generation.${generationStatus}`)}</span>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* NaraChef Configuration — hidden while NaraChef is disabled (see CHEF_ENABLED) */}
          {CHEF_ENABLED && (
          <div className="bg-card border border-border rounded-xl p-5">
            <h4 className="text-sm font-semibold text-foreground mb-1">{t("adminSettings.chefConfig.title")}</h4>
            <p className="text-xs text-muted-foreground mb-4">{t("adminSettings.chefConfig.description")}</p>

            {isChefConfigLoading ? (
              <div className="flex items-center py-4">
                <Loader2 className="h-4 w-4 text-muted-foreground animate-spin" />
                <span className="ml-2 text-sm text-muted-foreground">{t("adminSettings.chefConfig.loading")}</span>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Model inputs */}
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">
                    {t("adminSettings.chefConfig.routerModel")}
                  </label>
                  <input
                    type="text"
                    value={chefConfigs["vertex_ai_model"] ?? ""}
                    onChange={(e) => updateChefConfigValue("vertex_ai_model", e.target.value)}
                    placeholder="gemini-2.0-flash-001"
                    className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-nara-orange/50"
                  />
                  <p className="text-xs text-muted-foreground mt-1">{t("adminSettings.chefConfig.routerModelHint")}</p>
                </div>

                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">
                    {t("adminSettings.chefConfig.codeModel")}
                  </label>
                  <input
                    type="text"
                    value={chefConfigs["vertex_ai_code_model"] ?? ""}
                    onChange={(e) => updateChefConfigValue("vertex_ai_code_model", e.target.value)}
                    placeholder="gemini-2.5-pro"
                    className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-nara-orange/50"
                  />
                  <p className="text-xs text-muted-foreground mt-1">{t("adminSettings.chefConfig.codeModelHint")}</p>
                </div>

                {/* Collapsible prompt sections */}
                {[
                  { key: "root_prompt", label: t("adminSettings.chefConfig.rootPrompt") },
                  { key: "recipe_manager_prompt", label: t("adminSettings.chefConfig.managerPrompt") },
                  { key: "code_expert_prompt", label: t("adminSettings.chefConfig.codePrompt") },
                ].map(({ key, label }) => (
                  <div key={key} className="border border-border rounded-lg">
                    <button
                      onClick={() => togglePrompt(key)}
                      className="flex items-center justify-between w-full px-3 py-2 text-left"
                    >
                      <span className="text-xs font-medium text-muted-foreground">{label}</span>
                      <div className="flex items-center gap-2">
                        {chefConfigs[key] && (
                          <span className="text-xs text-nara-orange">{t("adminSettings.chefConfig.customized")}</span>
                        )}
                        {expandedPrompts[key] ? (
                          <ChevronDown className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-muted-foreground" />
                        )}
                      </div>
                    </button>
                    {expandedPrompts[key] && (
                      <div className="px-3 pb-3 space-y-2">
                        <AutoTextarea
                          value={chefConfigs[key] ?? ""}
                          onChange={(e) => updateChefConfigValue(key, e.target.value)}
                          placeholder={t("adminSettings.chefConfig.defaultPlaceholder")}
                          minRows={4}
                          className="w-full px-3 py-2 text-xs font-mono border border-border rounded-lg bg-background resize-y focus:outline-none focus:ring-2 focus:ring-nara-orange/50"
                        />
                        {chefConfigs[key] && (
                          <button
                            onClick={() => resetChefConfigValue(key)}
                            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                          >
                            <RotateCcw className="h-3 w-3" />
                            {t("adminSettings.chefConfig.resetDefault")}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                ))}

                {/* Save + Backup buttons */}
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleChefConfigSave}
                    disabled={chefConfigSaveStatus === "saving"}
                    className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-nara-orange hover:bg-nara-orange/90 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {chefConfigSaveStatus === "saving" ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4" />
                    )}
                    {chefConfigSaveStatus === "saving" ? t("adminSettings.chefConfig.saving") : t("adminSettings.chefConfig.save")}
                  </button>
                  <button
                    onClick={handleBackupDownload}
                    className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-foreground border border-border hover:bg-muted/50 rounded-lg transition-colors"
                  >
                    <Download className="h-4 w-4" />
                    {t("adminSettings.chefConfig.backup")}
                  </button>
                  {chefConfigSaveStatus === "saved" && (
                    <span className="flex items-center gap-1 text-sm text-green-600">
                      <Check className="h-4 w-4" />
                      {t("adminSettings.chefConfig.saved")}
                    </span>
                  )}
                  {chefConfigSaveStatus === "error" && (
                    <span className="text-sm text-red-500">{t("adminSettings.chefConfig.saveError")}</span>
                  )}
                </div>
              </div>
            )}
          </div>
          )}
          {/* API Keys */}
          <div className="bg-card border border-border rounded-xl p-5">
            <div className="flex items-center gap-2 mb-1">
              <Key className="h-4 w-4 text-muted-foreground" />
              <h4 className="text-sm font-semibold text-foreground">API Keys</h4>
            </div>
            <p className="text-xs text-muted-foreground mb-4">
              Manage API keys for the Admin REST API and MCP server. Each key can have its own IP whitelist.
            </p>

            {/* Created key banner — shown once after creation */}
            {createdKey && (
              <div className="mb-4 p-3 bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <Shield className="h-4 w-4 text-green-600" />
                  <span className="text-sm font-medium text-green-800 dark:text-green-200">
                    Key created — copy it now, it won't be shown again
                  </span>
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <code className="flex-1 text-xs font-mono bg-white dark:bg-black/20 px-2 py-1.5 rounded border border-green-200 dark:border-green-700 select-all break-all">
                    {keyVisible ? createdKey : "••••••••••••••••••••••••••••••••"}
                  </code>
                  <button
                    onClick={() => setKeyVisible((v) => !v)}
                    className="p-1.5 text-muted-foreground hover:text-foreground transition-colors"
                    title={keyVisible ? "Hide key" : "Show key"}
                  >
                    {keyVisible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                  <button
                    onClick={handleCopyKey}
                    className="flex items-center gap-1 px-2 py-1.5 text-xs font-medium text-green-700 dark:text-green-300 border border-green-300 dark:border-green-700 rounded hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors"
                  >
                    {keyCopied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                    {keyCopied ? "Copied" : "Copy"}
                  </button>
                </div>
                <button
                  onClick={() => { setCreatedKey(null); setKeyVisible(false); }}
                  className="mt-2 text-xs text-green-600 dark:text-green-400 hover:underline"
                >
                  Dismiss
                </button>
              </div>
            )}

            {apiKeyError && (
              <div className="mb-4 p-2 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg">
                <span className="text-xs text-red-600 dark:text-red-400">{apiKeyError}</span>
              </div>
            )}

            {/* Create new key form */}
            <div className="mb-4 p-3 bg-muted/30 rounded-lg space-y-3">
              <div className="flex items-center gap-2">
                <Plus className="h-4 w-4 text-muted-foreground" />
                <span className="text-xs font-medium text-foreground">Create new key</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Name</label>
                  <input
                    type="text"
                    value={newKeyName}
                    onChange={(e) => setNewKeyName(e.target.value)}
                    placeholder='e.g. "Claude Code", "Partner Agent"'
                    className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-nara-orange/50"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">
                    Allowed IPs <span className="text-muted-foreground font-normal">(comma-separated CIDRs, required)</span>
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={newKeyIps}
                      onChange={(e) => setNewKeyIps(e.target.value)}
                      placeholder="e.g. 203.0.113.0/24, 10.0.0.1/32"
                      className="flex-1 px-3 py-2 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-nara-orange/50"
                    />
                    <button
                      onClick={handleAddMyIp}
                      disabled={isFetchingIp}
                      className="flex items-center gap-1 px-3 py-2 text-xs font-medium text-foreground border border-border hover:bg-muted/50 rounded-lg transition-colors whitespace-nowrap disabled:opacity-50"
                      title="Add your current public IP"
                    >
                      {isFetchingIp ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <Globe className="h-3 w-3" />
                      )}
                      Add my IP
                    </button>
                  </div>
                </div>
              </div>
              <button
                onClick={handleCreateApiKey}
                disabled={isCreatingKey || !newKeyName.trim()}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-nara-orange hover:bg-nara-orange/90 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isCreatingKey ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Key className="h-4 w-4" />
                )}
                {isCreatingKey ? "Creating..." : "Generate Key"}
              </button>
            </div>

            {/* Key list */}
            {isApiKeysLoading ? (
              <div className="flex items-center py-4">
                <Loader2 className="h-4 w-4 text-muted-foreground animate-spin" />
                <span className="ml-2 text-sm text-muted-foreground">Loading keys...</span>
              </div>
            ) : apiKeys.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4">No API keys created yet.</p>
            ) : (
              <div className="space-y-2">
                {apiKeys.map((key) => (
                  <div
                    key={key.id}
                    className={`flex items-center justify-between p-3 border rounded-lg ${
                      key.active
                        ? "border-border bg-background"
                        : "border-border/50 bg-muted/20 opacity-60"
                    }`}
                  >
                    {editingKeyId === key.id ? (
                      <div className="flex-1 space-y-2">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          <div>
                            <label className="text-xs font-medium text-muted-foreground mb-1 block">Name</label>
                            <input
                              type="text"
                              value={editKeyName}
                              onChange={(e) => setEditKeyName(e.target.value)}
                              className="w-full px-2 py-1.5 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-nara-orange/50"
                            />
                          </div>
                          <div>
                            <label className="text-xs font-medium text-muted-foreground mb-1 block">Allowed IPs</label>
                            <div className="flex items-center gap-2">
                              <input
                                type="text"
                                value={editKeyIps}
                                onChange={(e) => setEditKeyIps(e.target.value)}
                                placeholder="e.g. 203.0.113.0/24, 10.0.0.1/32"
                                className="flex-1 px-2 py-1.5 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-nara-orange/50"
                              />
                              <button
                                onClick={handleAddMyIpToEdit}
                                disabled={isFetchingIp}
                                className="flex items-center gap-1 px-2 py-1.5 text-xs font-medium text-foreground border border-border hover:bg-muted/50 rounded-lg transition-colors whitespace-nowrap disabled:opacity-50"
                                title="Add your current public IP"
                              >
                                {isFetchingIp ? (
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                ) : (
                                  <Globe className="h-3 w-3" />
                                )}
                                Add my IP
                              </button>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleUpdateKey(key.id)}
                            disabled={isUpdatingKey}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-nara-orange hover:bg-nara-orange/90 rounded-lg transition-colors disabled:opacity-50"
                          >
                            {isUpdatingKey ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                            {isUpdatingKey ? "Saving..." : "Save"}
                          </button>
                          <button
                            onClick={cancelEditKey}
                            className="px-3 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground border border-border rounded-lg transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-foreground">{key.name}</span>
                            <code className="text-xs font-mono text-muted-foreground bg-muted/50 px-1.5 py-0.5 rounded">
                              {key.key_prefix}...
                            </code>
                            {key.active ? (
                              <span className="text-xs text-green-600 bg-green-50 dark:bg-green-950/30 px-1.5 py-0.5 rounded">
                                Active
                              </span>
                            ) : (
                              <span className="text-xs text-red-500 bg-red-50 dark:bg-red-950/30 px-1.5 py-0.5 rounded">
                                Revoked
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                            <span>
                              IPs: {key.allowed_ips.length > 0 ? key.allowed_ips.join(", ") : <span className="text-red-500">None (blocked)</span>}
                            </span>
                            {key.created_at && (
                              <span>Created: {new Date(key.created_at).toLocaleDateString()}</span>
                            )}
                            {key.last_used_at && (
                              <span>Last used: {new Date(key.last_used_at).toLocaleDateString()}</span>
                            )}
                          </div>
                        </div>
                        {key.active && (
                          <div className="flex items-center gap-1 ml-3">
                            <button
                              onClick={() => startEditKey(key)}
                              className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded transition-colors"
                              title="Edit key"
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleRevokeKey(key.id, key.name)}
                              className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/30 rounded transition-colors"
                              title="Revoke key"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
